import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import config from '../config.js';
import { serverMessage } from '../_helpers/errorMessage.js';
import '../styles/PerformerHome.css';

import cherryDecoration from '../assets/cherry-decoration.png';
import arcInnerRight from '../assets/avatar-arc-a.svg';
import arcOuterRight from '../assets/avatar-arc-b.svg';
import arcInnerLeft from '../assets/avatar-arc-c.svg';
import arcOuterLeft from '../assets/avatar-arc-d.svg';
import { FaUserCircle, FaPen } from 'react-icons/fa';

import BandLayoutPage from './partial/layout/BandLayoutPage.jsx';
import PaymentSetup from './partial/PaymentSetup.jsx';
import BandSettingsForm from './BandSettings.jsx';
import BandMembers from './BandMembers.jsx';
import ModeratorManagement from './partial/ModeratorManagement.jsx';
import StreamSourceSetup from './partial/StreamSourceSetup.jsx';
import MultistreamSetup from './partial/MultistreamSetup.jsx';
import OverlayIntegration from './partial/OverlayIntegration.jsx';
import LiveButton from './partial/LiveButton.jsx';
import GoLiveModal from './partial/modal/GoLiveModal.jsx';
import FollowButton from './partial/FollowButton.jsx';

import { useFetchWrapper } from '../_helpers/fetchWrapper.js';
import { isBandMember, getCurrentUserId } from '../_helpers/bandUtils.js';
import { normalizeStreamSource } from '../_helpers/streamSource.js';
import { allowedSocialMediaLinks, countHiddenSocialMediaLinks } from '../_helpers/socialMediaLinks.js';
import { useStreamStatus } from '../hooks/useStreamStatus.js';
import { useMultistreamStatus } from '../hooks/useMultistreamStatus.js';
import { FEED_CHECK_UNAVAILABLE_CODE, FEED_MISSING_CODE, isFeedGated } from '../_helpers/multistream.js';
import { useT } from '../contexts/LocaleContext.jsx';
import { BAND_INVITES_ENABLED } from '../config/features.js';
import { richMessage, withStrong } from '../_helpers/richMessage.jsx';
import { capturePerformerEvent } from '../_helpers/performerEvents.js';

// ENG-355: the Band Information form is no longer listed here; members reach it
// from the "Edit band" button on the profile card (ENG-354).
/** Whether a follower summary carries a count the server actually read. */
const hasCount = (summary) => Number.isFinite(summary?.followerCount);

const PerformerHomeSettings = ({ onPayment, onBandMembers, onModerators, onOverlayIntegration, onStreamSources, onSongshakeFeed }) => {
  const t = useT();
  return (
    <div className="performer-home-band-settings-menu">
      <h3>{t('performer.settings.title')}</h3>
      <div className="performer-home-band-settings-buttons">
        <button className="performer-home-band-settings-button" onClick={onPayment}>
          <span>{t('performer.settings.payment')}</span>
          <span className="performer-home-band-settings-arrow">›</span>
        </button>
        <button className="performer-home-band-settings-button" onClick={onBandMembers}>
          <span>{t('performer.settings.bandMembers')}</span>
          <span className="performer-home-band-settings-arrow">›</span>
        </button>
        <button className="performer-home-band-settings-button" onClick={onModerators}>
          <span>{t('performer.settings.moderators')}</span>
          <span className="performer-home-band-settings-arrow">›</span>
        </button>
        <button className="performer-home-band-settings-button" onClick={onOverlayIntegration}>
          <span>{t('performer.settings.overlayIntegration')}</span>
          <span className="performer-home-band-settings-arrow">›</span>
        </button>
        <button className="performer-home-band-settings-button" onClick={onStreamSources}>
          <span>{t('performer.settings.streamSource')}</span>
          <span className="performer-home-band-settings-arrow">›</span>
        </button>
        <button className="performer-home-band-settings-button" onClick={onSongshakeFeed} data-testid="performer-settings-songshake-feed">
          <span>{t('performer.settings.songshakeFeed')}</span>
          <span className="performer-home-band-settings-arrow">›</span>
        </button>
      </div>
    </div>
  );
};

/**
 * Views of the band page that manage the band (ENG-501). Overlay
 * Integration isn't one: it shows its own Access Denied, so a non-member
 * coming back from the Streamlabs OAuth flow is told why rather than
 * silently put back on the band home.
 */
const MEMBER_VIEWS = new Set(['payment', 'band-settings', 'band-members', 'moderators', 'stream-sources', 'songshake-feed']);

const PerformerHomeLoading = () => {
  const t = useT();
  return <div>{t('common.status.loading')}</div>;
};

// A band ID is a positive integer; anything else can't name a band.
const isBandId = (value) => /^[1-9]\d*$/.test(String(value ?? ''));

// Shown when the band doesn't exist (ENG-514); the page used to stay on
// "Loading..." for good.
const PerformerHomeNotFound = () => {
  const t = useT();
  return (
    <div className="performer-home-missing" role="alert" data-testid="band-not-found">
      <h2>{t('performer.missing.notFoundTitle')}</h2>
      <p>{t('performer.missing.notFoundBody')}</p>
      <Link to="/performers">{t('performer.missing.findPerformers')}</Link>
    </div>
  );
};

const PerformerHomeLoadFailed = ({ onRetry }) => {
  const t = useT();
  return (
    <div className="performer-home-missing" role="alert" data-testid="band-load-failed">
      <p>{t('performer.missing.loadFailed')}</p>
      <button type="button" onClick={onRetry}>{t('performer.missing.retry')}</button>
    </div>
  );
};

const PerformerHomeContent = ({
  band, currentView, isMember, isStreamingLive, bandid, paymentMessage, streamlabsMessage,
  onBack, onGoLive, onBandSettingsSuccess, onManageMembers, onBandInformation, settingsNav,
  canOpenDashboard = false, onFollowChange,
}) => {
  const navigate = useNavigate();
  const t = useT();
  // Display name of a follower, falling back to a generic label built from the id
  const followerName = (follower) => follower.Username || follower.Name || t('performer.followers.anonymousUser', { id: follower.UserID });
  // The settings views are for members only (ENG-501). A deep link
  // (?payment_setup=, /stripe_incomplete, ?streamlabs_connect=) can ask for
  // one before the band has loaded; for anyone else it is ignored and the
  // band home shows, so a non-member never mounts PaymentSetup and its
  // member-only /payment-status request.
  if (!isMember && MEMBER_VIEWS.has(currentView)) {
    currentView = 'home';
  }
  if (currentView === 'payment') {
    return <PaymentSetup band={band} onBack={onBack} paymentMessage={paymentMessage} />;
  }

  if (currentView === 'band-settings') {
    return <BandSettingsForm band={band} onBack={onBack} onSuccess={onBandSettingsSuccess} />;
  }

  if (currentView === 'band-members') {
    return <BandMembers band={band} onBack={onBack} onSuccess={onBandSettingsSuccess} />;
  }

  if (currentView === 'moderators') {
    return <ModeratorManagement onBack={onBack} onSuccess={onBandSettingsSuccess} />;
  }

  if (currentView === 'overlay-integration') {
    return (
      <OverlayIntegration
        band={band}
        isMember={isMember}
        onBack={onBack}
        streamlabsMessage={streamlabsMessage}
      />
    );
  }

  if (currentView === 'stream-sources') {
    return <StreamSourceSetup band={band} onBack={onBack} />;
  }

  if (currentView === 'songshake-feed') {
    return <MultistreamSetup band={band} onBack={onBack} />;
  }

  // Only allowlisted https links become hrefs; stored links may predate the
  // backend validator (see socialMediaLinks.js). Dropping one silently would
  // leave the band wondering where it went, so members are told how many are
  // hidden and sent to the Band Information form, where saving asks to fix them.
  const socialMediaLinks = allowedSocialMediaLinks(band.SocialMediaLinks);
  const hiddenSocialMediaLinks = isMember ? countHiddenSocialMediaLinks(band.SocialMediaLinks) : 0;
  const hasFollowerCount = hasCount(band.followerSummary);
  const followerCount = hasFollowerCount ? band.followerSummary.followerCount : 0;

  return (
    <div className="performer-home-page-content">
      {/* Left card: profile */}
      <div className="performer-home-profile" data-testid="performer-profile">
        <div className="performer-home-profile-avatar-wrap">
          <img src={arcOuterLeft}  className="performer-home-arc performer-home-arc--outer-left"  alt="" aria-hidden="true" />
          <img src={arcInnerLeft}  className="performer-home-arc performer-home-arc--inner-left"  alt="" aria-hidden="true" />
          <img src={arcOuterRight} className="performer-home-arc performer-home-arc--outer-right" alt="" aria-hidden="true" />
          <img src={arcInnerRight} className="performer-home-arc performer-home-arc--inner-right" alt="" aria-hidden="true" />
          {band.ProfileImageURL ? (
            <img src={band.ProfileImageURL} alt={t('performer.profile.bandImageAlt', { name: band.Name })} className="performer-home-profile-image" />
          ) : (
            <div className="performer-home-profile-avatar" aria-hidden="true">
              {band.Name?.[0]?.toUpperCase()}
            </div>
          )}
        </div>
        <div className="performer-home-profile-name-row">
          <h2 className="performer-home-page-title" data-testid="performer-band-name">{band.Name}</h2>
          <LiveButton bandId={bandid} isStreaming={isStreamingLive} className="performer-home" />
          <FollowButton bandId={bandid} viewerRole={band.viewer_role} onFollowChange={onFollowChange} />
        </div>
        {band.Description && (
          <p className="performer-home-profile-bio">{band.Description}</p>
        )}
        {/* ENG-354: members get an edit entry point on the profile card itself.
            Since ENG-355 the settings menu no longer lists Band Information, so
            the form is reached from this button and from the hidden-social-links
            notice below. */}
        {isMember && (
          <button type="button" className="performer-home-edit-band-btn" onClick={onBandInformation}>
            <FaPen aria-hidden="true" />
            <span>{t('performer.profile.editBand')}</span>
          </button>
        )}
        <div className="performer-home-meta">
          {band.Location && (
            <div className="performer-home-meta-row">
              <span className="performer-home-meta-label">{t('performer.profile.locationLabel')}</span>
              <span className="performer-home-meta-value">{band.Location}</span>
            </div>
          )}
          {band.WebsiteURL && (
            <div className="performer-home-meta-row">
              <span className="performer-home-meta-label">{t('performer.profile.websiteLabel')}</span>
              <a href={band.WebsiteURL} target="_blank" rel="noopener noreferrer" className="performer-home-meta-link">{band.WebsiteURL}</a>
            </div>
          )}
          {band.ContactEmail && (
            <div className="performer-home-meta-row">
              <span className="performer-home-meta-label">{t('performer.profile.emailLabel')}</span>
              <a href={`mailto:${band.ContactEmail}`} className="performer-home-meta-link">{band.ContactEmail}</a>
            </div>
          )}
          {socialMediaLinks.map((link, index) => (
            <div key={index} className="performer-home-meta-row">
              <span className="performer-home-meta-label">{index === 0 ? t('performer.profile.socialLabel') : ''}</span>
              <a href={link} target="_blank" rel="noopener noreferrer" className="performer-home-meta-link" data-testid="performer-social-link">{link}</a>
            </div>
          ))}
          {hiddenSocialMediaLinks > 0 && (
            <div className="performer-home-meta-row" role="status">
              <span className="performer-home-meta-label">{socialMediaLinks.length === 0 ? t('performer.profile.socialLabel') : ''}</span>
              <span className="performer-home-meta-notice">
                {t('performer.profile.hiddenSocialLinks', { count: hiddenSocialMediaLinks })}
                {' '}
                <button type="button" className="performer-home-meta-notice-button" onClick={onBandInformation}>
                  {t('performer.profile.fixInBandInformation')}
                </button>
              </span>
            </div>
          )}
        </div>
        <div className="performer-home-members-section">
          <span className="performer-home-section-label">{t('performer.profile.membersLabel')}</span>
          <div className="performer-home-members-list">
            {(band.Members || []).map(member => (
              <div key={member.UserID} className="performer-home-profile-member">
                {member.ProfileImageURL ? (
                  <img src={member.ProfileImageURL} alt={t('performer.profile.memberImageAlt', { name: member.Username })} className="performer-home-member-profile-image" />
                ) : (
                  <div className="performer-home-member-avatar" aria-hidden="true">
                    {member.Username?.[0]?.toUpperCase()}
                  </div>
                )}
                <span>{member.Username}</span>
              </div>
            ))}
            {/* Hidden until an invitation can be accepted (ENG-518, ENG-491). */}
            {isMember && BAND_INVITES_ENABLED && (
              <button className="performer-home-invite-btn" onClick={onManageMembers}>
                {t('performer.profile.inviteMember')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Middle column: Tonight/Go Live card + Followers card */}
      <div className="performer-home-middle-column">
        <div className="performer-home-tonight-card">
          <div className="performer-home-tonight-eyebrow">
            <span className="performer-home-tonight-label">{t('performer.tonight.label')}</span>
            <img src={cherryDecoration} alt="" className="performer-home-tonight-cherry" aria-hidden="true" />
          </div>
          <h3 className="performer-home-tonight-heading">{t('performer.tonight.heading')}</h3>
          <div className="performer-home-live-status">
            <span className="performer-home-live-dot" />
            <span>{t('performer.tonight.liveRequestsOpen')}</span>
          </div>
          {isMember && (
            <button className="performer-home-go-live-button" onClick={onGoLive} data-testid="performer-go-live">
              {t('performer.tonight.goLive')}
            </button>
          )}
          {/* The way back to the streamer dashboard while the band is live
              (ENG-513): nothing on the band page linked to it. */}
          {canOpenDashboard && isStreamingLive && (
            <button
              type="button"
              className="performer-home-go-live-button"
              onClick={() => navigate(`/performers/${bandid}/streamer`)}
              data-testid="performer-open-dashboard"
            >
              {t('performer.tonight.openDashboard')}
            </button>
          )}
          <p className="performer-home-shows-note">
            {withStrong(t('performer.tonight.noShowsScheduled'), { addOne: t('performer.tonight.addOne') })}
          </p>
        </div>

        {/* Everyone gets the follower count; who follows is for members
            (ENG-567, ENG-581). With no count from the server the card is
            left out rather than claiming "0 followers". */}
        {hasFollowerCount && (
        <div className="performer-home-followers-card" data-testid="performer-followers">
          <div className="performer-home-follower-count-row">
            {richMessage(
              t('performer.followers.count', { count: followerCount }),
              { number: <span className="performer-home-follower-number" data-testid="performer-follower-count">{followerCount}</span> },
              { text: (chunk) => <span className="performer-home-follower-word">{chunk}</span> }
            )}
          </div>
          {isMember && (
          <div className="performer-home-follower-section">
            <span className="performer-home-section-label">{t('performer.followers.recentLabel')}</span>
            <div className="followers-images-section">
              {band.followerSummary?.recentFollowers?.length > 0
                ? band.followerSummary.recentFollowers.slice(0, 5).map((follower, index) => (
                    <div key={index} className="follower-image-item">
                      {follower.ProfileImageURL ? (
                        <img
                          src={follower.ProfileImageURL}
                          alt={followerName(follower)}
                          className="follower-profile-image"
                          title={followerName(follower)}
                        />
                      ) : (
                        <FaUserCircle
                          className="follower-profile-image default-avatar"
                          title={followerName(follower)}
                        />
                      )}
                      <span className="follower-username">
                        {followerName(follower)}
                      </span>
                    </div>
                  ))
                : <span className="no-followers" data-testid="performer-no-recent-followers">{t('common.status.none')}</span>
              }
            </div>
          </div>
          )}
          {isMember && (
          <div className="performer-home-follower-section">
            <span className="performer-home-section-label">{t('performer.followers.longestLabel')}</span>
            <div className="followers-images-section">
              {band.followerSummary?.longestFollower ? (
                <div className="follower-image-item">
                  {band.followerSummary.longestFollower.ProfileImageURL ? (
                    <img
                      src={band.followerSummary.longestFollower.ProfileImageURL}
                      alt={followerName(band.followerSummary.longestFollower)}
                      className="follower-profile-image"
                      title={followerName(band.followerSummary.longestFollower)}
                    />
                  ) : (
                    <FaUserCircle
                      className="follower-profile-image default-avatar"
                      title={followerName(band.followerSummary.longestFollower)}
                    />
                  )}
                  <span className="follower-username">
                    {followerName(band.followerSummary.longestFollower)}
                  </span>
                </div>
              ) : (
                <span className="no-followers">{t('common.status.none')}</span>
              )}
            </div>
          </div>
          )}
          {isMember && (
            <button
              onClick={() => navigate(`/performers/${bandid}/followers`)}
              className="performer-home-view-followers-btn"
            >
              {t('performer.followers.viewAll')}
            </button>
          )}
        </div>
        )}
      </div>

      {/* Right column: Band Settings */}
      {settingsNav}
    </div>
  );
};

const PerformerHome = () => {
  const { bandid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const t = useT();
  const [band, setBand] = useState(null);
  // Why the band couldn't be shown: 'notFound' or 'failed' (ENG-514).
  const [loadProblem, setLoadProblem] = useState(null);
  const [currentView, setCurrentView] = useState('home');
  const [paymentMessage, setPaymentMessage] = useState(null);
  const [streamlabsMessage, setStreamlabsMessage] = useState(null);
  const [isGoLiveModalOpen, setIsGoLiveModalOpen] = useState(false);
  const [streamSource, setStreamSource] = useState(null);
  const [streamSourceUnavailable, setStreamSourceUnavailable] = useState(false);
  // Why going live failed (the server's words or a catalog message), shown
  // in the still-open modal (ENG-504).
  const [goLiveError, setGoLiveError] = useState(null);
  const [goingLive, setGoingLive] = useState(false);
  // Go Live answered songshake_feed_missing (ENG-763): the feed dropped
  // between the status read and the click. Cleared by the next status read.
  const [feedMissing, setFeedMissing] = useState(false);

  const fetchWrapperRef = useRef(useFetchWrapper());
  const isMember = useRef(false);
  // Members and queue moderators can run the dashboard (ENG-513).
  const [canOpenDashboard, setCanOpenDashboard] = useState(false);

  const { isLive: isStreamingLive } = useStreamStatus(bandid);
  // The SongShake feed gate (ENG-763), read while the Go Live modal is open
  // so it unlocks as soon as SongShake starts receiving the stream.
  const { status: feedStatus, refresh: refreshFeed } =
    useMultistreamStatus(bandid, { enabled: isGoLiveModalOpen && isMember.current });
  useEffect(() => {
    setFeedMissing(false);
  }, [feedStatus]);

  const fetchBandData = useCallback(async () => {
    if (!isBandId(bandid)) {
      setLoadProblem('notFound');
      return;
    }
    setLoadProblem(null);
    try {
      const data = await fetchWrapperRef.current.get(`${config.apiBaseUrl}/bands/${bandid}`);
      const bandData = {
        ...data,
        ID: data.id ?? data.ID,
        Name: data.name ?? data.Name,
        Location: data.location ?? data.Location,
        Description: data.description ?? data.Description,
        ProfileImageURL: data.profile_image_url ?? data.ProfileImageURL,
        SocialMediaLinks: data.social_media_links ?? data.SocialMediaLinks,
        WebsiteURL: data.website_url ?? data.WebsiteURL,
        ContactEmail: data.contact_email ?? data.ContactEmail,
        Members: data.members ?? data.Members ?? [],
        followerSummary: data.follower_summary ?? data.followerSummary,
      };
      setBand(bandData);
      sessionStorage.setItem('bandId', bandData.ID);
      // viewer_role (ENG-494) is the role source; a backend that predates it
      // still lists the members.
      isMember.current = data.viewer_role
        ? data.viewer_role === 'member'
        : isBandMember(bandData, getCurrentUserId());
      setCanOpenDashboard(isMember.current || data.viewer_role === 'moderator');
    } catch (error) {
      console.error('Error fetching band data:', error);
      setLoadProblem(error?.status === 404 || error?.status === 400 ? 'notFound' : 'failed');
    }
  }, [bandid]);

  useEffect(() => {
    fetchBandData();
  }, [fetchBandData]);

  useEffect(() => {
    // The band menu's Band Settings opens a settings view through router
    // state (ENG-513); the member-only views stay gated by MEMBER_VIEWS.
    if (location.state?.openView) {
      setCurrentView(location.state.openView);
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }

    if (location.state?.showPaymentSetup) {
      setCurrentView('payment');
      if (location.state.paymentMessage) {
        // StripeIncompleteRedirect hands over a catalog key rather than text.
        const { key, ...message } = location.state.paymentMessage;
        setPaymentMessage(key ? { ...message, text: t(key) } : message);
      }
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }

    const urlParams = new URLSearchParams(location.search);
    const paymentSetup = urlParams.get('payment_setup');

    if (paymentSetup) {
      setCurrentView('payment');

      switch (paymentSetup) {
        case 'stripe_success':
          setPaymentMessage({ type: 'info', text: t('performer.paymentMessages.stripeSuccess') });
          break;
        case 'stripe_refresh':
          setPaymentMessage({ type: 'info', text: t('performer.paymentMessages.stripeRefresh') });
          break;
        case 'stripe_incomplete':
          setPaymentMessage({ type: 'error', text: t('performer.paymentMessages.stripeIncomplete') });
          break;
        case 'paypal_success':
          setPaymentMessage({ type: 'success', text: t('performer.paymentMessages.paypalSuccess') });
          break;
        case 'paypal_error':
          setPaymentMessage({ type: 'error', text: t('performer.paymentMessages.paypalError') });
          break;
        default:
          setPaymentMessage({ type: 'info', text: t('performer.paymentMessages.completed') });
      }

      navigate(`/performers/${bandid}`, { replace: true });
    }

    const streamlabsConnect = urlParams.get('streamlabs_connect');

    if (streamlabsConnect) {
      setCurrentView('overlay-integration');
      setStreamlabsMessage(
        streamlabsConnect === 'success'
          ? { type: 'success', text: t('performer.streamlabsMessages.connected') }
          : { type: 'error', text: t('performer.streamlabsMessages.failed') }
      );
      navigate(`/performers/${bandid}`, { replace: true });
    }
  }, [location.state, location.pathname, location.search, navigate, bandid, t]);

  const handlePaymentClick = () => setCurrentView('payment');
  const handleBandInformationClick = () => setCurrentView('band-settings');
  const handleBandMembersClick = () => setCurrentView('band-members');
  const handleModeratorsClick = () => setCurrentView('moderators');
  const handleOverlayIntegrationClick = () => {
    setCurrentView('overlay-integration');
    setStreamlabsMessage(null);
  };

  const handleStreamSourcesClick = () => setCurrentView('stream-sources');
  const handleSongshakeFeedClick = () => setCurrentView('songshake-feed');
  const handleOpenFeedSetup = () => {
    setIsGoLiveModalOpen(false);
    setCurrentView('songshake-feed');
  };

  const handleBackToHome = () => {
    setCurrentView('home');
    setPaymentMessage(null);
    setStreamlabsMessage(null);
  };

  // A band has at most one stream source; the public read answers 404 when
  // none is configured. Going live is always confirmed in the modal, which
  // shows the source listeners will watch, says none is set up, or — when the
  // read failed for another reason — that it could not be checked, so the band
  // never goes live silently on the strength of a failed request.
  //
  // The SongShake feed (ENG-763) is read alongside, so the modal opens
  // already knowing whether Go Live has to wait for it.
  const handleGoLiveClick = async () => {
    let source = null;
    let sourceUnavailable = false;
    const feedRead = isMember.current ? refreshFeed() : null;
    try {
      source = normalizeStreamSource(
        await fetchWrapperRef.current.get(`${config.apiBaseUrl}/public/bands/${bandid}/stream/source`),
      );
    } catch (err) {
      sourceUnavailable = err?.status !== 404;
    }
    await feedRead;
    setStreamSource(source);
    setStreamSourceUnavailable(sourceUnavailable);
    setGoLiveError(null);
    setFeedMissing(false);
    setIsGoLiveModalOpen(true);
  };

  const handleGoLive = async (source) => {
    if (goingLive) return;
    setGoingLive(true);
    setGoLiveError(null);
    try {
      await fetchWrapperRef.current.post(`${config.apiBaseUrl}/bands/${bandid}/golive`);
      // ENG-888: only once the band is live; the show is read after.
      void capturePerformerEvent('go_live_clicked', { bandId: bandid, fetchWrapper: fetchWrapperRef.current });
      setIsGoLiveModalOpen(false);
      // With no stream source, going live lands on Stream Source setup rather
      // than the fan watch page (ENG-513), so the band can add one.
      if (source) {
        navigate(`/performers/${bandid}/streamer`);
      } else {
        setCurrentView('stream-sources');
      }
    } catch (error) {
      console.error('Error going live:', error);
      if (error?.code === FEED_MISSING_CODE) {
        // Same message as the gate; the next status read lifts it once the
        // feed is back.
        setFeedMissing(true);
        refreshFeed();
      } else if (error?.status === 503 && error?.code === FEED_CHECK_UNAVAILABLE_CODE) {
        setGoLiveError(t('events.goLive.feedCheckUnavailable'));
      } else {
        setGoLiveError(serverMessage(error) || t('events.goLive.failed'));
      }
    } finally {
      setGoingLive(false);
    }
  };

  const handleBandSettingsSuccess = () => {
    fetchBandData();
  };

  // The follower count moves with Follow / Unfollow, and back if it fails
  // (ENG-519). With no count from the server there is nothing to move, and
  // making one up from 0 would show a wrong number (ENG-581).
  const handleFollowChange = useCallback((delta) => {
    setBand((prev) => {
      if (!prev || !hasCount(prev.followerSummary)) return prev;
      const summary = prev.followerSummary;
      const followerCount = Math.max(0, summary.followerCount + delta);
      return { ...prev, followerSummary: { ...summary, followerCount } };
    });
  }, []);

  if (!band || loadProblem === 'notFound') {
    return (
      <BandLayoutPage showHamburgerMenu={false}>
        {loadProblem === 'notFound' ? (
          <PerformerHomeNotFound />
        ) : loadProblem === 'failed' ? (
          <PerformerHomeLoadFailed onRetry={fetchBandData} />
        ) : (
          <PerformerHomeLoading />
        )}
      </BandLayoutPage>
    );
  }

  // A failed status read doesn't gate: Go Live itself says whether the feed is missing.
  const goLiveFeed = feedMissing || isFeedGated(feedStatus) ? 'required' : null;

  const settingsNav = isMember.current ? (
    <PerformerHomeSettings
      onPayment={handlePaymentClick}
      onBandMembers={handleBandMembersClick}
      onModerators={handleModeratorsClick}
      onOverlayIntegration={handleOverlayIntegrationClick}
      onStreamSources={handleStreamSourcesClick}
      onSongshakeFeed={handleSongshakeFeedClick}
    />
  ) : null;

  return (
    <BandLayoutPage showHamburgerMenu={false} pageType="home">
      <PerformerHomeContent
        band={band}
        currentView={currentView}
        isMember={isMember.current}
        isStreamingLive={isStreamingLive}
        canOpenDashboard={canOpenDashboard}
        onFollowChange={handleFollowChange}
        bandid={bandid}
        paymentMessage={paymentMessage}
        streamlabsMessage={streamlabsMessage}
        onBack={handleBackToHome}
        onGoLive={handleGoLiveClick}
        onBandSettingsSuccess={handleBandSettingsSuccess}
        onManageMembers={handleBandMembersClick}
        onBandInformation={handleBandInformationClick}
        settingsNav={settingsNav}
      />
      <GoLiveModal
        isOpen={isGoLiveModalOpen}
        onClose={() => setIsGoLiveModalOpen(false)}
        onGoLive={handleGoLive}
        source={streamSource}
        sourceUnavailable={streamSourceUnavailable}
        error={goLiveError}
        isSubmitting={goingLive}
        feed={goLiveFeed}
        onOpenFeedSetup={handleOpenFeedSetup}
      />
    </BandLayoutPage>
  );
};

export default PerformerHome;
